// frontend/src/pages/SettingsPage.jsx
import React, { useEffect, useState } from 'react';
import { Badge, Box, Button, Flex, H1, InlineMessage, Panel, Text } from '@bigcommerce/big-design';
import { ArrowBackIcon } from '@bigcommerce/big-design-icons';
import { useNavigate } from 'react-router-dom';
import { getStoreHash } from '../api/config';

const SettingsPage = () => {
  const [status, setStatus] = useState(null); // { checkout_mode, installed, native_webhook_registered, native_extension_enabled }
  const [billing, setBilling] = useState(null); // { configured, subscription } | null while loading
  const [isSwitching, setIsSwitching] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [isTogglingExtension, setIsTogglingExtension] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [success, setSuccess] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const navigate = useNavigate();
  const storeHash = getStoreHash();

  useEffect(() => {
    if (!storeHash) return;

    fetch(`/api/checkout/status?store_hash=${storeHash}`)
      .then((res) => res.json())
      .then(setStatus)
      .catch((err) => console.error('Failed to load checkout status:', err));

    fetch(`/api/subscription?store_hash=${storeHash}`)
      .then((res) => res.json())
      .then(setBilling)
      .catch((err) => console.error('Failed to load subscription status:', err));
  }, [storeHash]);

  // Lets links like TrialExpiredPage's "Go to Settings to Subscribe" jump
  // straight to the Subscription panel instead of the top of the page - only
  // once billing has loaded, since the panel doesn't render until then.
  useEffect(() => {
    if (window.location.hash === '#subscription-panel' && billing) {
      document.getElementById('subscription-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [billing]);

  const switchMode = async (mode) => {
    setIsSwitching(true);
    setErrorMsg('');

    try {
      const res = await fetch('/api/checkout/mode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_hash: storeHash, mode }),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data?.error || 'Unknown error');

      setStatus((prev) => ({ ...prev, ...data }));
      setSuccess(mode === 'native' ? 'Switched to Native Checkout.' : 'Switched to Custom Checkout.');
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'Failed to switch checkout mode.');
    } finally {
      setIsSwitching(false);
    }
  };

  const handleDisable = async () => {
    setIsDisabling(true);
    setErrorMsg('');

    try {
      const res = await fetch(`/api/checkout/uninstall?store_hash=${storeHash}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data?.error || 'Unknown error');

      setSuccess('Custom checkout disabled successfully! Redirecting...');
      setTimeout(() => {
        navigate('/setup-checkout');
      }, 2000);
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'Failed to disable checkout.');
    } finally {
      setIsDisabling(false);
    }
  };

  const toggleExtension = async (enable) => {
    setIsTogglingExtension(true);
    setErrorMsg('');

    try {
      const res = await fetch(`/api/checkout-extension/${enable ? 'install' : 'uninstall'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_hash: storeHash }),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data?.error || 'Unknown error');

      setStatus((prev) => ({ ...prev, ...data }));
      setSuccess(enable ? 'Real-time shipping-based fee updates enabled.' : 'Real-time shipping-based fee updates disabled.');
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'Failed to update the Checkout Extension.');
    } finally {
      setIsTogglingExtension(false);
    }
  };

  const handleSubscribe = async () => {
    setIsSubscribing(true);
    setErrorMsg('');

    try {
      // redirect_url is computed server-side, not sent from here - this page
      // runs inside BigCommerce's dashboard iframe, so window.location here is
      // our own domain, not the BigCommerce wrapper URL a "Back to app" link
      // needs to return to. See SubscriptionsController#create_checkout.
      const res = await fetch('/api/subscription/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_hash: storeHash }),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data?.error || 'Unknown error');

      // This page runs inside BigCommerce's dashboard iframe. Setting
      // window.location here would only navigate the iframe, which BigCommerce
      // blocks/intercepts for cross-origin destinations and silently reloads
      // the app instead. Navigate the top-level window to actually reach the
      // BigCommerce-hosted subscription checkout.
      window.top.location.href = data.checkout_url;
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'Failed to start subscription checkout.');
      setIsSubscribing(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    setErrorMsg('');

    try {
      const res = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_hash: storeHash }),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data?.error || 'Unknown error');

      setBilling((prev) => ({ ...prev, subscription: null }));
      setSuccess('Subscription cancelled.');
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'Failed to cancel subscription.');
    } finally {
      setIsCancelling(false);
    }
  };

  const checkoutMode = status?.checkout_mode; // 'native' | 'custom' | undefined (still loading)

  return (
    <Box margin="xxLarge" padding="large">
      {/* Header with Back Button */}
      <Flex alignItems="center" marginBottom="large">
        <Button
          variant="subtle"
          iconLeft={<ArrowBackIcon />}
          onClick={() => navigate('/fees')}
          marginRight="medium"
        >
          Back
        </Button>
        <H1 margin="none">App Settings</H1>
      </Flex>

      {errorMsg && (
        <InlineMessage type="error" messages={[{ text: errorMsg }]} marginBottom="large" />
      )}
      {success && (
        <InlineMessage type="success" messages={[{ text: success }]} marginBottom="large" />
      )}

      {/* Settings panel: contains all configuration cards */}
      <Panel header="Settings" marginBottom="large">
        {/* Checkout Integration card */}
        <Panel
          header="Checkout Integration"
          marginBottom="medium"
          badge={
            checkoutMode
              ? { label: checkoutMode === 'native' ? 'Native Checkout Active' : 'Custom Checkout Active', variant: 'success' }
              : undefined
          }
        >
          {checkoutMode === 'native' && (
            <>
              <Flex alignItems="center" marginBottom="medium">
                <Text margin="none" marginRight="xSmall">Cart-change webhook:</Text>
                <Badge
                  label={status?.native_webhook_registered ? 'Registered' : 'Not registered'}
                  variant={status?.native_webhook_registered ? 'success' : 'danger'}
                />
              </Flex>
              <Text marginBottom="medium">
                Fees sync automatically to BigCommerce's standard checkout{status?.native_webhook_registered ? '' : ', but the webhook isn’t registered yet, so fees won’t update automatically. Try switching modes again to re-register it.'}
              </Text>
              <Button
                variant="secondary"
                onClick={() => switchMode('custom')}
                isLoading={isSwitching}
                disabled={isSwitching}
              >
                Switch to Custom Checkout (Advanced)
              </Button>
            </>
          )}

          {checkoutMode === 'custom' && (
            <>
              <Text marginBottom="medium">
                Currently using <strong>Custom Checkout</strong>. Most stores don't need this;
                Native Checkout delivers fees the same way with no custom checkout script to maintain.
              </Text>
              <Button
                variant="secondary"
                onClick={() => switchMode('native')}
                isLoading={isSwitching}
                disabled={isSwitching}
              >
                Switch to Native Checkout (Recommended)
              </Button>
            </>
          )}

          {!checkoutMode && <Text color="secondary60">Loading current checkout mode...</Text>}
        </Panel>

        {/* Experimental card: beta Checkout Extension, only meaningful in Native mode */}
        {checkoutMode === 'native' && (
          <Panel
            header="Experimental"
            marginBottom="medium"
            badge={{
              label: status?.native_extension_enabled ? 'Enabled' : 'Not enabled',
              variant: status?.native_extension_enabled ? 'success' : 'secondary',
            }}
          >
            <Text marginBottom="medium">
              Uses BigCommerce's beta Checkout Extensions API to refresh fees immediately when a
              shopper changes shipping method, instead of waiting for the next natural checkout
              refresh. Fee delivery itself always works the same either way; this only affects
              how quickly a shipping-driven fee change shows up mid-session. Since this depends on a
              BigCommerce beta feature, expect it to be less stable than the rest of the app.
            </Text>
            <Button
              variant="secondary"
              onClick={() => toggleExtension(!status?.native_extension_enabled)}
              isLoading={isTogglingExtension}
              disabled={isTogglingExtension}
            >
              {status?.native_extension_enabled
                ? 'Disable real-time shipping-based fee updates'
                : 'Enable real-time shipping-based fee updates (beta)'}
            </Button>
          </Panel>
        )}

        {/* Legacy custom-checkout uninstall flow card, only relevant while on Custom mode */}
        {checkoutMode === 'custom' && (
          <>
            <Panel header="App Settings" marginBottom="medium">
              <Text color="secondary60" marginBottom="medium">
                Before uninstalling this app from BigCommerce, you should disable the custom checkout to restore your store's default checkout experience.
              </Text>

              <Button
                variant="secondary"
                onClick={handleDisable}
                isLoading={isDisabling}
                disabled={isDisabling}
              >
                {isDisabling ? 'Disabling...' : 'Disable Custom Checkout'}
              </Button>
            </Panel>

            <Box padding="medium" backgroundColor="warning10" borderRadius="normal">
              <Text as="h4" bold marginBottom="small">⚠️ Important Instructions:</Text>
              <Box as="ol" marginLeft="medium">
                <li>Click "Disable Custom Checkout" button above</li>
                <li>Wait for the success message</li>
                <li>Then go to BigCommerce admin to uninstall the app</li>
              </Box>
              <Text bold marginTop="medium">Do not uninstall the app before disabling the custom checkout!</Text>
            </Box>
          </>
        )}
      </Panel>

      {/* Subscription panel: hidden entirely until billing is actually configured
          (BC_PARTNER_ACCOUNT_UUID etc. set), so there's no broken/empty UI in the
          meantime. See UnifiedBillingService. */}
      {billing?.configured && (
        <Box id="subscription-panel">
          <Panel
            header="Subscription"
            marginBottom="large"
            badge={{
              label: billing.subscription
                ? (billing.subscription.status === 'CANCELLED' ? 'Cancelled' : 'Active')
                : billing.trial?.expired ? 'Trial Expired' : 'Free Trial',
              variant: billing.subscription
                ? (billing.subscription.status === 'CANCELLED' ? 'warning' : 'success')
                : billing.trial?.expired ? 'danger' : 'warning',
            }}
          >
            {billing.subscription ? (
              <>
                <Text margin="none">
                  <strong>{billing.subscription.currency} {billing.subscription.price}</strong> / {billing.subscription.interval?.toLowerCase()}
                </Text>
                {billing.subscription.status === 'CANCELLED' ? (
                  <Text marginTop="medium">
                    Cancelled. Fees will keep working through{' '}
                    <strong>{new Date(billing.subscription.current_period_end).toLocaleDateString()}</strong>, the
                    date you already paid through; BigCommerce doesn't issue a refund for the unused remainder, and
                    after that date you'll need to resubscribe.
                  </Text>
                ) : (
                  <Button
                    variant="secondary"
                    marginTop="medium"
                    onClick={handleCancelSubscription}
                    isLoading={isCancelling}
                    disabled={isCancelling}
                  >
                    Cancel Subscription
                  </Button>
                )}
              </>
            ) : (
              <>
                {billing.trial && !billing.trial.expired && (
                  <Text marginBottom="medium">
                    You have <strong>{billing.trial.days_remaining} day{billing.trial.days_remaining === 1 ? '' : 's'}</strong> left in your free trial. Subscribe anytime; it won't start billing until your trial actually ends.
                  </Text>
                )}
                {billing.trial?.expired && (
                  <InlineMessage
                    type="error"
                    messages={[{ text: 'Your free trial has ended. Subscribe to keep managing and delivering fees.' }]}
                    marginBottom="medium"
                  />
                )}
                {!billing.trial && (
                  <Text marginBottom="medium">You don't have an active subscription for this app.</Text>
                )}
                <Button
                  variant="primary"
                  onClick={handleSubscribe}
                  isLoading={isSubscribing}
                  disabled={isSubscribing}
                >
                  Subscribe
                </Button>
              </>
            )}
          </Panel>
        </Box>
      )}
    </Box>
  );
};

export default SettingsPage;
